import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      {/* Header */}
      <header className="glass-effect sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link">
            <img
              src="/WhiteLogo.png"
              alt="Alvron Partners"
              className="h-10 md:h-12"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </nav>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-8" data-testid="terms-title">
          Terms and Conditions
        </h1>
        <p className="text-muted-foreground mb-12">
          Last Updated: January 30, 2026
        </p>

        <div className="prose prose-invert max-w-none space-y-8 text-muted-foreground">
          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">1. Agreement to Terms</h2>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and Alvron Partners ("Company," "we," "us," or "our") concerning your access to and use of the www.alvronpartners.com website and purchase of our digital products and services.
            </p>
            <p>
              By accessing our website, creating an account, or purchasing any digital products or services, you agree to be bound by these Terms. If you disagree with any part of these Terms, you must not access the website or purchase our products.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">2. Definitions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">"Digital Products"</strong> refers to any downloadable content, online courses, reports, templates, software, or other digital goods sold through our platform.</li>
              <li><strong className="text-foreground">"Services"</strong> refers to investment banking advisory, consulting, and other professional services offered by Alvron Partners.</li>
              <li><strong className="text-foreground">"Content"</strong> refers to all text, images, videos, data, and other materials available on our website.</li>
              <li><strong className="text-foreground">"Account"</strong> refers to your registered user account on our platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">3. Digital Products and Purchases</h2>
            
            <h3 className="text-xl text-foreground mt-6 mb-3">3.1 Product Descriptions</h3>
            <p>
              We make every effort to display and describe our digital products accurately. However, we do not warrant that product descriptions, pricing, or other content is accurate, complete, reliable, current, or error-free.
            </p>

            <h3 className="text-xl text-foreground mt-6 mb-3">3.2 Pricing and Payment</h3>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>All prices are displayed in Indian Rupees (INR) unless otherwise specified</li>
              <li>Prices are subject to change without notice</li>
              <li>Payment must be made in full at the time of purchase</li>
              <li>We accept major credit cards, debit cards, and other payment methods as displayed at checkout</li>
              <li>All payments are processed through secure, PCI-DSS compliant payment gateways</li>
              <li>You agree to provide accurate and complete payment information</li>
            </ul>

            <h3 className="text-xl text-foreground mt-6 mb-3">3.3 Order Confirmation</h3>
            <p>
              Upon successful payment, you will receive an order confirmation email with details of your purchase. This email serves as your receipt and proof of purchase.
            </p>

            <h3 className="text-xl text-foreground mt-6 mb-3">3.4 Delivery of Digital Products</h3>
            <p>
              Digital products will be delivered electronically via:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Direct download link sent to your registered email address</li>
              <li>Access through your user account dashboard</li>
              <li>Cloud-based access for applicable services</li>
            </ul>
            <p className="mt-4">
              Delivery is typically instant upon payment confirmation. If you do not receive your digital product within 24 hours, please contact us at contact@alvronpartners.com.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">4. Refund Policy for Digital Products</h2>
            
            <h3 className="text-xl text-foreground mt-6 mb-3">4.1 General Policy</h3>
            <p>
              Due to the nature of digital products, all sales are generally considered final. Once a digital product has been delivered or accessed, it cannot be "returned" in the traditional sense.
            </p>

            <h3 className="text-xl text-foreground mt-6 mb-3">4.2 Refund Eligibility</h3>
            <p>Refunds may be considered in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-foreground">Technical Issues:</strong> If you are unable to access or download the product due to technical problems on our end</li>
              <li><strong className="text-foreground">Duplicate Purchase:</strong> If you accidentally purchased the same product twice</li>
              <li><strong className="text-foreground">Significant Misrepresentation:</strong> If the product is materially different from its description</li>
              <li><strong className="text-foreground">Non-Delivery:</strong> If you did not receive your product and we cannot resolve the delivery issue</li>
            </ul>

            <h3 className="text-xl text-foreground mt-6 mb-3">4.3 Refund Request Process</h3>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Refund requests must be submitted within 7 days of purchase</li>
              <li>Submit requests via email to contact@alvronpartners.com with your order number</li>
              <li>Each request is reviewed on a case-by-case basis</li>
              <li>Approved refunds will be processed within 10-14 business days</li>
              <li>Refunds will be credited to the original payment method</li>
            </ul>

            <h3 className="text-xl text-foreground mt-6 mb-3">4.4 Non-Refundable Items</h3>
            <p>The following are not eligible for refunds:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Products that have been fully accessed or downloaded</li>
              <li>Customized or personalized digital products</li>
              <li>Services that have been rendered</li>
              <li>Subscription fees after the service period has begun</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">5. Intellectual Property Rights</h2>
            
            <h3 className="text-xl text-foreground mt-6 mb-3">5.1 Our Content</h3>
            <p>
              All content on our website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and software, is the property of Alvron Partners or its content suppliers and is protected by international copyright laws.
            </p>

            <h3 className="text-xl text-foreground mt-6 mb-3">5.2 License for Digital Products</h3>
            <p>Upon purchase of a digital product, you are granted a limited, non-exclusive, non-transferable license to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Download and use the product for personal or internal business purposes</li>
              <li>Store the product on devices you own or control</li>
            </ul>
            <p className="mt-4">You may NOT:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Redistribute, resell, or share the digital product with third parties</li>
              <li>Modify, adapt, or create derivative works</li>
              <li>Remove any copyright or proprietary notices</li>
              <li>Use the product for any unlawful purpose</li>
              <li>Upload the product to file-sharing services</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">6. User Accounts</h2>
            <p>When you create an account with us, you agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="mt-4">
              We reserve the right to terminate accounts, remove content, or cancel orders at our discretion if we believe you have violated these Terms.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">7. Disclaimer of Warranties</h2>
            <p>
              OUR WEBSITE AND DIGITAL PRODUCTS ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Implied warranties of merchantability</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement</li>
              <li>Accuracy or completeness of content</li>
              <li>Uninterrupted or error-free service</li>
            </ul>
            <p className="mt-4">
              <strong className="text-foreground">Financial Disclaimer:</strong> Digital products and content provided by Alvron Partners are for informational and educational purposes only. They do not constitute financial advice, investment advice, or any other type of professional advice. Always consult with qualified professionals before making financial decisions.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">8. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALVRON PARTNERS AND ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
              <li>Damages resulting from your use or inability to use our services</li>
              <li>Any unauthorized access to your data or transmissions</li>
              <li>Any third-party conduct or content on the service</li>
            </ul>
            <p className="mt-4">
              Our total liability for any claims under these Terms shall not exceed the amount you paid to us for the specific product or service giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">9. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Alvron Partners and its affiliates, licensors, and service providers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees arising out of or relating to your violation of these Terms or your use of our website and services.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">10. Governing Law and Jurisdiction</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Mumbai, Maharashtra, India.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">11. Dispute Resolution</h2>
            <p>
              Any dispute, controversy, or claim arising out of or relating to these Terms shall first be attempted to be resolved through good-faith negotiation. If the dispute cannot be resolved through negotiation within 30 days, it shall be submitted to binding arbitration in accordance with the Arbitration and Conciliation Act, 1996 of India.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">12. Severability</h2>
            <p>
              If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall be enforced to the fullest extent under law.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">13. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and any other legal notices published by us on the website, constitute the entire agreement between you and Alvron Partners concerning our services.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">14. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of our services after any changes constitutes acceptance of the modified Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-foreground mb-4">15. Contact Information</h2>
            <p>For questions about these Terms and Conditions, please contact us at:</p>
            <div className="mt-4 p-6 bg-card border border-white/5">
              <p><strong className="text-foreground">Alvron Partners</strong></p>
              <p className="mt-2">18, Rajan Pada Mindspace</p>
              <p>Malad West, Mumbai, India</p>
              <p className="mt-2">Email: contact@alvronpartners.com</p>
            </div>
          </section>

          <section className="border-t border-white/10 pt-8 mt-8">
            <p className="text-sm">
              By using our website or purchasing our digital products, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card py-8 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Alvron Partners. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-muted-foreground hover:text-foreground text-sm transition-colors">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
